import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc } from '/imports/types';

/**
 * Per-fleet overrides of the chat message catalog.
 *
 * OVERRIDE-ONLY: a row exists solely because someone changed that message for
 * that scope. There is no row per (fleet x message) -- 12 fleets x 36 messages
 * would be 432 rows that are mostly identical, and changing a shared message
 * would mean editing it twelve times.
 *
 * Resolution order, most specific first:
 *   1. this fleet's override        (fleetId = the bot's fleet)
 *   2. the global override          (fleetId = null)
 *   3. the code default             (server/mogbot/messages.ts)
 *
 * Blank text is treated as absent at every level, so clearing a field in the
 * admin falls back rather than chatting an empty string.
 */
export interface BotMessage extends Doc {
  /** `${fleetId ?? 'default'}:${key}` -- natural dedupe, one row per scope+key. */
  _id: string;
  /** null = the global override applied to every fleet without its own. */
  fleetId: string | null;
  /** A key from the MESSAGES catalog. */
  key: string;
  text: string;
}

export const BotMessages = new Mongo.Collection<BotMessage>('botMessages');

/** Deterministic id so an upsert cannot create a duplicate for the same scope. */
export const botMessageId = (fleetId: string | null, key: string) => `${fleetId ?? 'default'}:${key}`;

/** One catalog entry merged with whatever overrides exist for the chosen scope. */
export interface MessageRow {
  key: string;
  label: string;
  description: string;
  /** placeholder -> meaning, rendered as help text on the field. */
  tokens: Record<string, string>;
  /** The hardcoded default from the code. */
  codeDefault: string;
  /** The global override, if any. */
  globalText: string | null;
  /** This fleet's override, if any (null when viewing the global scope). */
  fleetText: string | null;
  /** What a bot in this scope would actually say. */
  effective: string;
  /** Where `effective` came from. */
  source: 'fleet' | 'global' | 'code';
}

export const listBotMessages = DeclareMethod<{ fleetId?: string | null }, {
  rows: MessageRow[];
  fleetName: string | null;
}>('listBotMessages');

/** Upsert an override. Empty/whitespace text DELETES the override (reverts). */
export const setBotMessage = DeclareMethod<{ fleetId?: string | null; key: string; text: string }, {
  ok: boolean;
  error?: string;
  effective?: string;
  source?: 'fleet' | 'global' | 'code';
}>('setBotMessage');

/** Drop every override for a scope -- the one-click revert for a whole fleet. */
export const resetBotMessages = DeclareMethod<{ fleetId?: string | null }, { removed: number }>('resetBotMessages');
